import React, { useMemo } from "react";
import { Form } from "react-crud-form";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { FormField, FormItem, FormMessage } from "ui/components/form";
import { Textarea } from "ui/components/textarea";
import { FormLabel } from "components/form";
import { FormActions } from "components/form/actions";
import { getRequiredFields } from "components/form/utils";
import { PermissionControlledInput } from "permissions/components/input";
import { invalidateAllQueries } from "utils/utils";
import { challengeSchema } from "./schema";
import { createChallenge, fetchChallenge, updateChallenge } from "./api";
import { CHALLENGES_COLLECTION } from "../../constants";

const FIELDS = [
  { name: "challenge", label: "Challenge" },
  { name: "solution", label: "Solution" },
];

const ChallengeForm = ({ jobId, challengeId }) => {
  const navigate = useNavigate();
  const form = useForm({
    resolver: zodResolver(challengeSchema),
    defaultValues: { challenge: "", solution: "" },
  });
  const requiredFields = useMemo(() => getRequiredFields(challengeSchema), []);

  const handleRead = (id) => fetchChallenge({ challengeId: id });
  const handleCreate = async (context) => {
    await createChallenge({ item: { ...context.values, job: jobId } });
    toast.success("Challenge Created Successfully");
    invalidateAllQueries();
  };
  const handleUpdate = async (id, context) => {
    await updateChallenge({ challengeId: id, item: context.dirtyValues });
    toast.success("Challenge Updated Successfully");
    invalidateAllQueries();
  };

  return (
    <Form
      form={form}
      create={handleCreate}
      read={handleRead}
      update={handleUpdate}
      itemId={challengeId || "+"}
      schema={challengeSchema}
    >
      {({
        isCreating,
        createItem,
        isNewItem,
        isUpdating,
        updateItem,
        isDirty,
      }) => (
        <div className="space-y-6">
          {FIELDS.map(({ name, label }) => (
            <FormField
              key={name}
              control={form.control}
              name={name}
              render={({ field }) => (
                <FormItem>
                  <PermissionControlledInput
                    rule={isNewItem ? "create:*" : "update:*"}
                    collection={CHALLENGES_COLLECTION}
                    type="form"
                  >
                    <FormLabel required={requiredFields.includes(field.name)}>
                      {label}
                    </FormLabel>
                    <Textarea
                      className="min-h-[120px]"
                      {...field}
                      value={field.value ?? ""}
                    />
                  </PermissionControlledInput>
                  <FormMessage />
                </FormItem>
              )}
            />
          ))}
          <FormActions
            isNewItem={isNewItem}
            isCreating={isCreating}
            isUpdating={isUpdating}
            isDirty={isDirty}
            createItem={() => createItem().then(() => navigate(-1))}
            updateItem={() => updateItem().then(() => navigate(-1))}
            showCancel={false}
            collection={CHALLENGES_COLLECTION}
            rule={isNewItem ? "create:*" : "update:*"}
          />
        </div>
      )}
    </Form>
  );
};

export default ChallengeForm;
